import { useCallback } from 'react'

import { matchPath, useHistory, useLocation } from 'react-router-dom'

import { safetyMetricMetaData } from '../api/safety'
import { useBuildingContext } from '../contexts/buildingContext'
import { blockMobileFloorMapNavigation } from '../utils/mobileFloorMapAvailability'

import { PATH_STRINGS, useGeneratedPaths } from './useGeneratedPaths'

export const useNavigation = () => {
  const { projectId, floorId, pointId, scheduleId } = useBuildingContext().state
  const {
    generateFloorPath,
    generatePointPath,
    generatePointFullPath,
    generateProjectSafetyPath,
    generateProjectSafetyMetricDetailPath,
    generateProjectSafetyMetricDetailFloorPath,
    generateProjectSafetyMetricDetailFloorPointPath,
    generateSafetyGalleryPath,
    generateManagePointsFloorPath,
    generateManageNoGoZonesFloorPath,
    generateManageZonesFloorPath,
    generateManageTargetZonesFloorPath,
    generateProjectProgressPath,
    generateProjectProgressFloorPath,
    generateProjectProgressFloorPointPath,
    generateManageSchedulesPath,
    generateManageEquipmentFloorPath,
    generateManageUsersProjectUserPath,
    generateProjectProgressChartsPath,
    generateProjectProgressReportsPath,
    generateAdminProjectPath,
  } = useGeneratedPaths()

  const history = useHistory()
  const { pathname } = useLocation()

  const pushWithQueryParams = useCallback(
    (path: string, queryDict?: Record<string, string>) => {
      let pathWithParams = path

      if (queryDict) {
        pathWithParams += '?' + new URLSearchParams(queryDict)
      }

      return history.push(pathWithParams)
    },
    [history],
  )

  const navigateToFloor = useCallback(
    (floorId: string) => history.replace(generateFloorPath(projectId, floorId)),
    // eslint-disable-next-line
    [history, projectId],
  )
  const navigateToPoint = useCallback(
    (pointId: string) =>
      history.replace(generatePointPath(projectId, floorId, pointId)),
    // eslint-disable-next-line
    [history, floorId],
  )
  const navigateToPointFullscreen = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper queryDict type
    (pointId: string, queryDict?: any) =>
      history.replace(
        generatePointFullPath(projectId, floorId, pointId) +
          '?' +
          new URLSearchParams(queryDict),
      ), // eslint-disable-next-line
    [history, floorId],
  )
  const navigateToCurrentPointFullscreen = useCallback(
    () => history.push(generatePointFullPath(projectId, floorId, pointId)),
    // eslint-disable-next-line
    [history, floorId, pointId],
  )
  const navigateToCurrentPoint = useCallback(
    () => history.push(generatePointPath(projectId, floorId, pointId)),
    // eslint-disable-next-line
    [history, floorId, pointId],
  )

  const navigateToSafety = useCallback(
    (categorySlug?: string, floorCode?: string) => {
      if (floorCode && blockMobileFloorMapNavigation()) {
        return
      }

      const paramsDict: Record<string, string> = {
        ...(categorySlug ? { category: categorySlug } : {}),
        ...(floorCode ? { floor: floorCode } : {}),
      }

      return pushWithQueryParams(
        generateProjectSafetyPath(projectId),
        paramsDict,
      )
    },
    // eslint-disable-next-line
    [history, projectId],
  )

  const navigateToSafetyMetricDetail = useCallback(
    (categorySlug?: string) => {
      if (
        categorySlug &&
        safetyMetricMetaData[categorySlug as keyof typeof safetyMetricMetaData]
          ?.display_mode === 'floor_plan' &&
        blockMobileFloorMapNavigation()
      ) {
        return
      }

      history.push(
        generateProjectSafetyMetricDetailPath(projectId, categorySlug),
      )
    },
    // eslint-disable-next-line
    [history, projectId],
  )

  const navigateToSafetyMetricDetailFloor = useCallback(
    (floorId: string, categorySlug?: string) => {
      if (blockMobileFloorMapNavigation()) {
        return
      }

      history.push(
        generateProjectSafetyMetricDetailFloorPath(
          projectId,
          floorId,
          categorySlug,
        ),
      )
    },
    // eslint-disable-next-line
    [history, projectId],
  )

  const navigateToSafetyMetricDetailFloorPoint = useCallback(
    (
      pointId: string,
      categorySlug?: string,
      observationDate?: Date,
      itemId?: string | number,
    ) => {
      if (blockMobileFloorMapNavigation()) {
        return
      }

      const queryDict = {
        ...(observationDate ? { date: observationDate.toISOString() } : {}),
        ...(categorySlug ? { category: categorySlug } : {}),
        ...(itemId ? { itemId: itemId.toString() } : {}),
      }

      return pushWithQueryParams(
        generateProjectSafetyMetricDetailFloorPointPath(
          projectId,
          floorId,
          pointId,
        ),
        queryDict,
      )
    },
    // eslint-disable-next-line
    [pushWithQueryParams, projectId, floorId, pointId],
  )

  const navigateToSafetyGallery = useCallback(
    () => history.push(generateSafetyGalleryPath(projectId)),
    // eslint-disable-next-line
    [history, projectId],
  )

  const navigateToProgressOverview = useCallback(
    () => history.push(generateProjectProgressPath(projectId)),
    // eslint-disable-next-line
    [history, projectId],
  )

  const navigateToProgressFloor = useCallback(
    (
      floorId: string,
      observationDate?: Date,
      activeTracker?: string,
      itemId?: string | number,
    ) => {
      if (blockMobileFloorMapNavigation()) {
        return
      }

      const queryDict = {
        ...(observationDate ? { date: observationDate.toISOString() } : {}),
        ...(activeTracker ? { tracker: activeTracker } : {}),
        ...(itemId != null && itemId !== ''
          ? { itemId: itemId.toString() }
          : {}),
      }
      return pushWithQueryParams(
        generateProjectProgressFloorPath(projectId, floorId),
        queryDict,
      )
    },
    // eslint-disable-next-line
    [history, projectId, floorId],
  )

  const navigateToProgressFloorMulti3d = useCallback(
    (
      floorId: string,
      options?: {
        trackers?: string[]
        observationDate?: Date
        returnTracker?: string
        itemId?: string | number
      },
    ) => {
      if (blockMobileFloorMapNavigation()) {
        return
      }

      const queryDict: Record<string, string> = {
        combined3d: '1',
      }
      if (options?.trackers?.length) {
        queryDict.trackers = options.trackers.join(',')
      }
      if (options?.observationDate) {
        queryDict.date = options.observationDate.toISOString()
      }
      if (options?.returnTracker) {
        queryDict.tracker = options.returnTracker
      }
      if (options?.itemId != null && options.itemId !== '') {
        queryDict.itemId = options.itemId.toString()
      }
      return pushWithQueryParams(
        generateProjectProgressFloorPath(projectId, floorId),
        queryDict,
      )
    },
    [pushWithQueryParams, generateProjectProgressFloorPath, projectId],
  )

  const navigateToProgressFloorPoint = useCallback(
    (
      pointId: string,
      observationDate?: Date,
      activeTracker?: string,
      itemId?: string | number,
    ) => {
      if (blockMobileFloorMapNavigation()) {
        return
      }

      const queryDict = {
        ...(observationDate ? { date: observationDate.toISOString() } : {}),
        ...(activeTracker ? { tracker: activeTracker } : {}),
        ...(itemId ? { itemId: itemId.toString() } : {}),
      }

      return pushWithQueryParams(
        generateProjectProgressFloorPointPath(projectId, floorId, pointId),
        queryDict,
      )
    },
    // eslint-disable-next-line
    [pushWithQueryParams, projectId, floorId, pointId],
  )

  const navigateToAdminZone = useCallback(() => {
    const projectMatch =
      matchPath<{ projectId: string }>(pathname, {
        path: PATH_STRINGS.project,
        exact: false,
      }) ||
      matchPath<{ projectId: string }>(pathname, {
        path: PATH_STRINGS.adminProject,
        exact: false,
      })

    const currentProjectId = projectMatch?.params.projectId
    if (currentProjectId) {
      history.push(generateAdminProjectPath(currentProjectId))
      return
    }

    history.push(PATH_STRINGS.adminZone)
  }, [history, pathname, generateAdminProjectPath])

  const navigateToManagePointsFloor = useCallback(
    floorId => history.push(generateManagePointsFloorPath(projectId, floorId)),
    [history, projectId, generateManagePointsFloorPath],
  )

  const navigateToManageNoGoZonesFloor = useCallback(
    floorId =>
      history.push(generateManageNoGoZonesFloorPath(projectId, floorId)),
    [history, projectId, generateManageNoGoZonesFloorPath],
  )

  const navigateToManageZonesFloor = useCallback(
    floorId => history.push(generateManageZonesFloorPath(projectId, floorId)),
    [history, projectId, generateManageZonesFloorPath],
  )

  const navigateToManageTargetZonesFloor = useCallback(
    floorId =>
      history.push(generateManageTargetZonesFloorPath(projectId, floorId)),
    [history, projectId, generateManageTargetZonesFloorPath],
  )

  const navigateToManageSchedules = useCallback(
    (floorId, scheduleId) =>
      history.push(generateManageSchedulesPath(projectId, floorId, scheduleId)),
    [history, projectId, generateManageSchedulesPath],
  )

  const navigateToManageSchedulesFloor = useCallback(
    floorId =>
      history.push(generateManageSchedulesPath(projectId, floorId, scheduleId)),
    [history, projectId, scheduleId, generateManageSchedulesPath],
  )

  const navigateToManageEquipmentFloor = useCallback(
    floorId =>
      history.push(generateManageEquipmentFloorPath(projectId, floorId)),
    [history, projectId, generateManageEquipmentFloorPath],
  )

  const navigateToUserProfile = useCallback(
    () => history.push(PATH_STRINGS.userProfile),
    [history],
  )

  const navigateToUserProfilePassword = useCallback(
    () => history.push(PATH_STRINGS.userProfilePassword),
    [history],
  )

  const navigateToUserProfileNotificationPreferences = useCallback(
    () => history.push(PATH_STRINGS.userProfileNotificationPreferences),
    [history],
  )

  const navigateToUserProfileIntegrations = useCallback(
    () => history.push(PATH_STRINGS.userProfileIntegrations),
    [history],
  )

  const navigateToUserProfileScheduleImport = useCallback(
    () => history.push(PATH_STRINGS.userProfileScheduleImport),
    [history],
  )

  const navigateToUserProfileTools = useCallback(
    () => history.push(PATH_STRINGS.userProfileTools),
    [history],
  )

  const navigateToManageUsersUser = useCallback(
    (userId: string) =>
      history.push(generateManageUsersProjectUserPath(projectId, userId)),
    [history, generateManageUsersProjectUserPath, projectId],
  )

  const navigateToProjectProgressCharts = useCallback(
    (floor?: string, tracker?: string) => {
      const paramsDict: Record<string, string> = {
        ...(floor ? { floor: floor } : {}),
        ...(tracker ? { tracker: tracker } : {}),
      }

      return pushWithQueryParams(
        generateProjectProgressChartsPath(projectId),
        paramsDict,
      )
    },
    [pushWithQueryParams, generateProjectProgressChartsPath, projectId],
  )

  const navigateToProjectReports = useCallback(
    (documentId?: number) => {
      const paramsDict: Record<string, string> = {
        ...(documentId ? { document: documentId.toString() } : {}),
      }

      return pushWithQueryParams(
        generateProjectProgressReportsPath(projectId),
        paramsDict,
      )
    },
    [pushWithQueryParams, generateProjectProgressReportsPath, projectId],
  )

  return {
    navigateToFloor,
    navigateToCurrentPointFullscreen,
    navigateToCurrentPoint,
    navigateToPoint,
    navigateToPointFullscreen,
    navigateToSafety,
    navigateToSafetyMetricDetail,
    navigateToSafetyMetricDetailFloor,
    navigateToSafetyMetricDetailFloorPoint,
    navigateToSafetyGallery,
    navigateToAdminZone,
    navigateToManagePointsFloor,
    navigateToManageNoGoZonesFloor,
    navigateToManageZonesFloor,
    navigateToManageTargetZonesFloor,
    navigateToProgressOverview,
    navigateToProgressFloor,
    navigateToProgressFloorPoint,
    navigateToProgressFloorMulti3d,
    navigateToManageSchedules,
    navigateToManageSchedulesFloor,
    navigateToManageEquipmentFloor,
    navigateToUserProfile,
    navigateToUserProfilePassword,
    navigateToUserProfileNotificationPreferences,
    navigateToUserProfileIntegrations,
    navigateToUserProfileScheduleImport,
    navigateToUserProfileTools,
    navigateToManageUsersUser,
    navigateToProjectProgressCharts,
    navigateToProjectReports,
  }
}
